// Content map for DISEASE / CONDITION detail pages.

export const DISEASE_CONTENT = {
  "fatty-liver-treatment": {
    shortTitle: "Fatty Liver Disease",
    title: "Fatty Liver Treatment in Varanasi — Diagnosis & Reversal",
    subtitle:
      "Non-alcoholic fatty liver (NAFLD/MASLD) is now the most common chronic liver disease in India. The good news — caught in time, it is almost completely reversible.",
    seoTitle: "Fatty Liver Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription:
      "Diagnosis and reversal of fatty liver (NAFLD/MASLD) and NASH in Varanasi. FibroScan, structured weight loss and metabolic management with Dr. Indresh Dixit.",
    sections: [
      {
        id: "what", eyebrow: "Overview", heading: "What is fatty liver?",
        body: `Fatty liver disease is the accumulation of excess fat inside liver cells. It is divided into NAFLD (non-alcoholic) and AFLD (alcohol-related). NAFLD has three stages — simple fatty liver, NASH (with inflammation), and cirrhosis.\n\nMost people in stage 1 have no symptoms. The disease is silent until inflammation begins — and that is where early detection pays off.`,
      },
      {
        id: "symptoms", eyebrow: "Symptoms", heading: "What you might notice — or not.",
        bullets: [
          "Often no symptoms (most common)",
          "Mild dull right-upper abdominal discomfort",
          "Easy tiredness, low energy",
          "Slowly enlarging abdomen / waistline",
          "Borderline-high liver enzymes on a routine test",
          "Skin tags, dark patches around the neck (insulin resistance)",
        ],
      },
      {
        id: "diagnosis", eyebrow: "Tests we use", heading: "Precise — not panicked.",
        body: `A focused workup looks at three things — how fatty, how inflamed, how scarred. A typical sequence: liver enzymes (SGOT/SGPT), a lipid + diabetes panel, abdominal ultrasound, and FibroScan to quantify fat (CAP score) and fibrosis (kPa).\n\nLiver biopsy is rarely needed — only when the picture is unclear or to rule out other causes.`,
        sidenote: "FibroScan in 10 minutes gives a clear number for both fat content and scarring — and is repeated every 6–12 months to track improvement.",
      },
      {
        id: "treatment", eyebrow: "Treatment", heading: "A structured plan that actually works.",
        body: `Fatty liver responds to a coordinated plan — diet, weight loss, control of metabolic factors (sugar, lipids), and select medications when inflammation is high. Even a 5–10% reduction in body weight reverses early fatty liver in most patients.\n\nNewer treatments — including GLP-1 medications for selected patients — are discussed when indicated.`,
      },
      {
        id: "when", eyebrow: "When to consult", heading: "Get evaluated early.",
        bullets: [
          "Liver enzymes (SGPT/SGOT) above normal on routine test",
          "Diabetes + central obesity",
          "Family history of cirrhosis",
          "Persistent upper-right abdominal discomfort",
          "Yellowness, swelling in legs, or fluid in abdomen (urgent)",
        ],
      },
    ],
    faq: [
      { q: "Is fatty liver reversible?", a: "Yes — in early stages (NAFLD without significant fibrosis), structured weight loss and metabolic control fully reverse it." },
      { q: "What is a normal FibroScan value?", a: "Liver stiffness <7 kPa is generally normal; 7–10 indicates significant fibrosis; >10 needs detailed evaluation." },
      { q: "Do I need medicine, or only lifestyle change?", a: "Many improve with diet and weight loss alone. Medicines are added when inflammation is significant or metabolic control is poor." },
      { q: "Can fatty liver cause cancer?", a: "Long-standing NASH-cirrhosis carries a risk of liver cancer (HCC). Early reversal — before cirrhosis — is therefore important." },
    ],
    related: [
      { label: "FibroScan", to: "/procedures/fibroscan" },
      { label: "Liver Cirrhosis", to: "/diseases/liver-cirrhosis-treatment" },
      { label: "Jaundice", to: "/symptoms/jaundice-treatment" },
    ],
    schema: {
      name: "Fatty Liver Disease (NAFLD/MASLD/NASH)",
      alternateName: ["NAFLD", "MASLD", "NASH"],
      signOrSymptom: ["Often asymptomatic", "Right-upper abdominal discomfort", "Fatigue", "Elevated SGPT/SGOT"],
      riskFactor: ["Central obesity", "Type 2 diabetes", "High triglycerides", "Sedentary lifestyle", "PCOS"],
      possibleTreatment: ["Structured weight loss", "Diet modification", "Metabolic control", "FibroScan monitoring"],
    },
  },

  "liver-cirrhosis-treatment": {
    shortTitle: "Liver Cirrhosis",
    title: "Liver Cirrhosis Treatment in Varanasi",
    subtitle:
      "Cirrhosis is advanced liver scarring. Early stages can be stabilised and lived with for decades — but only if recognised and managed proactively.",
    seoTitle: "Liver Cirrhosis Treatment in Varanasi — Dr. Indresh Dixit",
    seoDescription:
      "Long-term cirrhosis management — variceal screening, ascites care, encephalopathy prevention and transplant referral when needed. Varanasi.",
    sections: [
      {
        id: "what", eyebrow: "Overview", heading: "What cirrhosis means.",
        body: `Cirrhosis is the end-stage of chronic liver injury — scar tissue replaces healthy liver. The liver loses its ability to make proteins, clear toxins and process blood. Early (compensated) cirrhosis often has few symptoms; decompensated cirrhosis brings complications.\n\nWith the right plan, many patients live well for 10–20+ years.`,
      },
      {
        id: "symptoms", eyebrow: "Symptoms", heading: "Warning signs by stage.",
        bullets: [
          "Compensated: fatigue, mild itching, spider naevi",
          "Decompensated: ascites (fluid in abdomen)",
          "Variceal bleeding (vomiting blood / black stool)",
          "Hepatic encephalopathy (confusion, sleep reversal)",
          "Jaundice, easy bruising, swelling of legs",
        ],
      },
      {
        id: "causes", eyebrow: "Why it happens", heading: "The common causes in our practice.",
        bullets: [
          "Hepatitis B / C", "Alcohol-related liver disease",
          "NAFLD / NASH cirrhosis",
          "Autoimmune hepatitis",
          "Hereditary (Wilson's, haemochromatosis)",
          "Biliary disease (PBC, PSC)",
        ],
      },
      {
        id: "diagnosis", eyebrow: "Tests", heading: "Confirm, stage and monitor.",
        body: `Blood work (CBC, LFT, INR, albumin), FibroScan, abdominal ultrasound with Doppler, and an upper GI endoscopy to screen for varices. Imaging (CT/MRI) is added when liver lesions are seen. Liver biopsy is occasional.\n\nMELD and Child-Pugh scores are used to track severity.`,
      },
      {
        id: "treatment", eyebrow: "Treatment", heading: "Cause + complication management.",
        body: `Treat the underlying cause (antivirals for hepatitis, alcohol cessation, metabolic control for NASH). Add beta-blockers / banding for varices, diuretics + salt restriction for ascites, lactulose + rifaximin for encephalopathy. Surveillance every 6 months for hepatocellular carcinoma. Transplant referral when indicated.`,
      },
    ],
    faq: [
      { q: "Is cirrhosis reversible?", a: "Established cirrhosis is largely irreversible, but disease progression can be halted and complications managed. Some early cases of cirrhosis can show improvement when the cause is removed." },
      { q: "Can I drink alcohol?", a: "No — even small amounts accelerate progression in any cirrhosis. Complete abstinence is required." },
      { q: "How often do I need scans?", a: "An ultrasound + AFP every 6 months for HCC screening. Endoscopy for varices every 1–3 years depending on findings." },
      { q: "When is liver transplant considered?", a: "When MELD score >15 with persistent decompensation, or specific complications (intractable ascites, recurrent encephalopathy, early HCC)." },
    ],
    related: [
      { label: "Fatty Liver", to: "/diseases/fatty-liver-treatment" },
      { label: "Hepatitis B", to: "/diseases/hepatitis-b-treatment" },
      { label: "FibroScan", to: "/procedures/fibroscan" },
    ],
    schema: {
      name: "Liver Cirrhosis",
      signOrSymptom: ["Fatigue", "Ascites", "Variceal bleeding", "Jaundice", "Encephalopathy"],
      riskFactor: ["Hepatitis B/C", "Alcohol", "NASH", "Autoimmune hepatitis"],
      possibleTreatment: ["Antiviral therapy", "Beta-blockers", "Variceal banding", "Diuretics", "Lactulose", "Liver transplant"],
    },
  },

  "hepatitis-b-treatment": {
    shortTitle: "Hepatitis B",
    title: "Hepatitis B Treatment in Varanasi",
    subtitle:
      "Chronic Hepatitis B is silent for years. Modern antivirals control it almost completely — provided someone monitors and adjusts the plan over time.",
    seoTitle: "Hepatitis B Treatment in Varanasi — Dr. Indresh Dixit (Hepatology)",
    seoDescription:
      "Diagnosis and long-term treatment of chronic Hepatitis B in Varanasi. Modern antivirals, family screening and HCC surveillance with Dr. Indresh Dixit.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "Acute vs chronic Hepatitis B.",
        body: `Acute HBV usually clears in adults within 6 months. In ~5% of adults (and most children infected at birth) it becomes chronic. Chronic HBV silently damages the liver over decades, raising risk of cirrhosis and liver cancer.\n\nWith timely treatment, life expectancy is normal.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "Mostly silent — until late.",
        bullets: ["Often no symptoms", "Fatigue, low-grade fever", "Mild jaundice in acute phase", "Right-upper abdominal discomfort", "Joint aches"] },
      { id: "tests", eyebrow: "Tests", heading: "HBsAg, HBV-DNA, liver assessment.",
        body: `Initial: HBsAg, anti-HBc IgM/IgG, HBeAg, HBe-antibody, HBV-DNA viral load, LFT, FibroScan. Imaging when indicated. Family members are tested and vaccinated.\n\nMonitoring frequency depends on phase — viral suppression takes regular review.`,
        sidenote: "All household contacts and partners should be tested. Vaccination of negative contacts is free and lifelong.",
      },
      { id: "treatment", eyebrow: "Treatment", heading: "Lifelong control with one daily tablet.",
        body: `Modern oral antivirals (tenofovir, entecavir, TAF) suppress HBV-DNA to undetectable in over 95% of patients. Treatment is daily, indefinite for most, and well-tolerated. We monitor HBV-DNA, LFT and FibroScan periodically.\n\nHCC surveillance every 6 months is standard.` },
      { id: "prevention", eyebrow: "Prevention", heading: "Vaccination works.",
        bullets: ["HBV vaccine — 3 doses, fully protective", "Mandatory in newborns", "Recommended for all household contacts", "Safe in pregnancy"],
      },
    ],
    faq: [
      { q: "Can Hepatitis B be cured?", a: "Functional cure (HBsAg loss) is uncommon but possible; viral suppression to undetectable is the realistic, life-extending goal — and it is achieved in the vast majority." },
      { q: "Will I need lifelong medication?", a: "Yes, in most chronic cases. Stopping can lead to flare. The drugs are safe and well-tolerated long-term." },
      { q: "Can I marry / have children?", a: "Absolutely. With proper antenatal management, transmission to baby is preventable." },
      { q: "Is alcohol safe?", a: "No. Alcohol accelerates HBV-related liver injury — avoid completely." },
    ],
    related: [
      { label: "Hepatitis C", to: "/diseases/hepatitis-c-treatment" },
      { label: "Liver Cirrhosis", to: "/diseases/liver-cirrhosis-treatment" },
      { label: "FibroScan", to: "/procedures/fibroscan" },
    ],
    schema: {
      name: "Chronic Hepatitis B",
      alternateName: ["HBV"],
      signOrSymptom: ["Often asymptomatic", "Fatigue", "Mild jaundice (acute)", "Right-upper abdominal discomfort"],
      riskFactor: ["Perinatal transmission", "Unsafe blood transfusion", "Shared needles", "Unprotected sex"],
      possibleTreatment: ["Tenofovir", "Entecavir", "TAF", "Long-term monitoring", "HCC surveillance"],
    },
  },

  "hepatitis-c-treatment": {
    shortTitle: "Hepatitis C",
    title: "Hepatitis C Treatment in Varanasi",
    subtitle:
      "Hepatitis C is now genuinely curable. A 12-week oral course clears the virus in >95% of patients — provided the diagnosis is made.",
    seoTitle: "Hepatitis C Treatment in Varanasi — Cure with DAAs | Dr. Indresh Dixit",
    seoDescription:
      "Diagnosis and cure of chronic Hepatitis C in Varanasi with modern direct-acting antivirals (DAAs). 95%+ SVR with Dr. Indresh Dixit.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "The most treatable chronic viral hepatitis.",
        body: `Hepatitis C is a bloodborne virus that mostly causes chronic infection. Untreated, it leads to cirrhosis and liver cancer over decades. Modern direct-acting antivirals (DAAs) cure most patients in 8–12 weeks of oral therapy.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "Largely silent.",
        bullets: ["Often discovered on routine LFT/serology", "Fatigue, joint pain", "Mild jaundice (uncommon)", "Late: signs of cirrhosis"],
      },
      { id: "diagnosis", eyebrow: "Diagnosis", heading: "Antibody, then RNA, then genotype.",
        body: `Anti-HCV antibody is the screening test. Positive cases need HCV-RNA (viral load) to confirm active infection. Genotype was previously needed but pangenotypic regimens have made it less critical. FibroScan stages fibrosis.` },
      { id: "treatment", eyebrow: "Treatment", heading: "Oral DAAs for 8–12 weeks.",
        body: `Pangenotypic regimens — sofosbuvir+velpatasvir, glecaprevir+pibrentasvir — clear HCV in >95% of patients with a daily oral course. Side-effects are mild. We confirm sustained virologic response (SVR) at 12 weeks post-treatment.`,
        sidenote: "SVR12 (undetectable virus 12 weeks after stopping treatment) = cure in current terms.",
      },
      { id: "after", eyebrow: "After cure", heading: "What happens once you are cured.",
        bullets: [
          "Repeat HCV-RNA at 12 weeks (SVR12)",
          "Annual LFT and FibroScan if fibrosis was present",
          "HCC screening every 6 months if cirrhotic at start",
          "Avoid re-infection (no needle sharing, safe blood)",
        ],
      },
    ],
    faq: [
      { q: "Is HCV curable in 12 weeks?", a: "Yes — direct-acting antivirals cure over 95% of patients in 8 or 12 weeks." },
      { q: "Are the medicines covered by insurance?", a: "Yes, most insurance plans cover DAA therapy for confirmed HCV. Generic versions are also affordable in India." },
      { q: "Can I infect my family?", a: "HCV is bloodborne, not commonly spread by routine contact. Sharing toothbrushes, razors or needles is a risk." },
      { q: "What if I have already developed cirrhosis?", a: "Cure still helps — it stops further progression and reduces HCC risk substantially, though it does not reverse established cirrhosis." },
    ],
    related: [
      { label: "Hepatitis B", to: "/diseases/hepatitis-b-treatment" },
      { label: "Liver Cirrhosis", to: "/diseases/liver-cirrhosis-treatment" },
      { label: "Jaundice", to: "/symptoms/jaundice-treatment" },
    ],
    schema: {
      name: "Chronic Hepatitis C",
      alternateName: ["HCV"],
      signOrSymptom: ["Often asymptomatic", "Fatigue", "Joint pain", "Late: cirrhosis features"],
      riskFactor: ["Unsafe injections", "Blood transfusion pre-2000", "IV drug use", "Tattoo/piercing with unsterile equipment"],
      possibleTreatment: ["Sofosbuvir + Velpatasvir", "Glecaprevir + Pibrentasvir", "8–12 weeks oral therapy", "SVR12 confirmation"],
    },
  },

  "gerd-treatment": {
    shortTitle: "GERD",
    title: "GERD Treatment in Varanasi",
    subtitle:
      "Gastro-Esophageal Reflux Disease (GERD) is when reflux happens often enough to damage the esophagus or affect quality of life. It is treatable, mostly without surgery.",
    seoTitle: "GERD Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription: "Long-term GERD / acid reflux management in Varanasi by Dr. Indresh Dixit. Endoscopy, PPI, lifestyle and surgery referral when needed.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "When acidity becomes a disease.",
        body: `GERD is reflux that occurs often (≥twice a week), causes symptoms or damages the esophagus. It can present with classical heartburn, throat symptoms (chronic cough, hoarseness), or atypical chest pain.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "Classical and atypical.",
        bullets: ["Heartburn, sour reflux", "Regurgitation of food", "Chronic dry cough at night", "Hoarseness or sore throat in mornings", "Chest pain (mimics cardiac)", "Dental erosions"],
      },
      { id: "diagnosis", eyebrow: "Tests", heading: "Endoscopy is the workhorse.",
        body: `Upper GI Endoscopy looks for erosive esophagitis, hiatus hernia, Barrett's esophagus and rules out malignancy. 24-hour pH-impedance monitoring is added when symptoms persist despite treatment.`,
        sidenote: "Anyone with reflux + alarm features (dysphagia, weight loss, anaemia) needs endoscopy, not just empirical PPI.",
      },
      { id: "treatment", eyebrow: "Treatment", heading: "Step-up plan.",
        body: `Lifestyle (weight loss, head-end elevation, late dinner avoidance) + PPI for 8–12 weeks. Step down to on-demand PPI. Persistent cases get pH-impedance testing and either escalation or anti-reflux surgery / endoscopic anti-reflux procedures.` },
      { id: "barretts", eyebrow: "Long-term watch", heading: "Why surveillance matters.",
        body: `Long-standing GERD can cause Barrett's esophagus — a pre-malignant change. Surveillance endoscopy is recommended for confirmed Barrett's.` },
    ],
    faq: [
      { q: "Is daily PPI safe long-term?", a: "Yes, at the lowest effective dose. We aim to step down whenever possible." },
      { q: "When is surgery considered?", a: "When patients fail PPI, have large hiatus hernia, regurgitation as the dominant symptom, or prefer not to be on long-term medication." },
      { q: "What is Barrett's esophagus?", a: "A change in the lining of the lower esophagus due to chronic reflux. It is pre-malignant — needs surveillance, not panic." },
      { q: "Can GERD be cured?", a: "Symptoms can be controlled in nearly everyone. The underlying anatomy (hiatus hernia, lower-sphincter weakness) may persist, hence ongoing care." },
    ],
    related: [
      { label: "Upper GI Endoscopy", to: "/procedures/upper-gi-endoscopy" },
      { label: "Acidity & Heartburn", to: "/symptoms/acidity-treatment-varanasi" },
      { label: "Gastritis", to: "/diseases/gastritis-treatment" },
    ],
    schema: {
      name: "Gastro-Esophageal Reflux Disease (GERD)",
      alternateName: ["Acid Reflux Disease"],
      signOrSymptom: ["Heartburn", "Regurgitation", "Chronic cough", "Hoarseness", "Chest pain"],
      riskFactor: ["Hiatus hernia", "Obesity", "Pregnancy", "Smoking", "Late dinners"],
      possibleTreatment: ["PPI therapy", "Lifestyle modification", "Anti-reflux surgery", "Endoscopic anti-reflux procedures"],
    },
  },

  "gastritis-treatment": {
    shortTitle: "Gastritis",
    title: "Gastritis Treatment in Varanasi",
    subtitle:
      "Inflammation of the stomach lining — usually from H. pylori, painkillers, or alcohol. Symptoms vary, but the work-up is straightforward.",
    seoTitle: "Gastritis Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription: "H. pylori eradication, NSAID-related gastritis and chronic gastritis treatment in Varanasi by Dr. Indresh Dixit.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "What gastritis means.",
        body: `Gastritis is inflammation of the stomach lining. It can be acute or chronic, erosive or non-erosive. The common causes in our region are H. pylori, NSAID use and alcohol.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "How it presents.",
        bullets: ["Burning in upper abdomen", "Nausea, occasional vomiting", "Bloating, early satiety", "Loss of appetite", "Iron-deficiency anaemia in chronic cases"],
      },
      { id: "causes", eyebrow: "Why it happens", heading: "Causes we see.",
        bullets: ["H. pylori infection", "Painkillers (NSAIDs)", "Alcohol", "Stress-related (critical illness)", "Bile reflux", "Autoimmune (less common)"],
      },
      { id: "diagnosis", eyebrow: "Diagnosis", heading: "Endoscopy + biopsy where needed.",
        body: `Upper GI Endoscopy with biopsy confirms gastritis, looks for H. pylori, identifies erosions, and rules out other diagnoses (ulcer, cancer). Stool antigen / urea breath test are non-invasive H. pylori options when endoscopy isn't planned.` },
      { id: "treatment", eyebrow: "Treatment", heading: "Cause-specific plan.",
        body: `H. pylori — 14-day eradication course. NSAID-induced — stop NSAID + PPI. Erosive — PPI 4–8 weeks. Chronic atrophic — periodic surveillance. Diet and lifestyle measures support the medical plan.`,
        sidenote: "Confirm H. pylori eradication 4+ weeks after treatment with breath / stool antigen testing — without checking, we don't know if it worked.",
      },
    ],
    faq: [
      { q: "How is H. pylori treated?", a: "A 14-day course of two antibiotics + a PPI. Modern regimens have >85% cure rates." },
      { q: "Can gastritis become cancer?", a: "Untreated chronic atrophic gastritis with intestinal metaplasia carries a small long-term risk. Surveillance prevents progression." },
      { q: "How long to stay on PPI?", a: "Usually 4–8 weeks, then taper. Long-term therapy is reserved for selected indications." },
      { q: "Are dietary changes enough?", a: "Helpful, but rarely enough alone — most patients also need targeted medical therapy." },
    ],
    related: [
      { label: "Peptic Ulcer", to: "/diseases/peptic-ulcer-treatment" },
      { label: "Upper GI Endoscopy", to: "/procedures/upper-gi-endoscopy" },
      { label: "GERD", to: "/diseases/gerd-treatment" },
    ],
    schema: {
      name: "Gastritis",
      signOrSymptom: ["Upper abdominal burning", "Nausea", "Bloating", "Early satiety", "Anaemia (chronic)"],
      riskFactor: ["H. pylori", "NSAID use", "Alcohol", "Stress", "Bile reflux"],
      possibleTreatment: ["H. pylori eradication", "PPI therapy", "NSAID withdrawal", "Surveillance for atrophic gastritis"],
    },
  },

  "pancreatitis-treatment": {
    shortTitle: "Pancreatitis",
    title: "Pancreatitis Treatment in Varanasi (Acute & Chronic)",
    subtitle:
      "Acute pancreatitis is a sudden, severe upper-abdominal illness. Chronic pancreatitis is a long, painful disease with digestive consequences. Both need specialist care.",
    seoTitle: "Pancreatitis Treatment in Varanasi (Acute & Chronic) — Dr. Indresh Dixit",
    seoDescription:
      "Hospital-grade pancreatitis care in Varanasi — acute pancreatitis admission, chronic pancreatitis management and ERCP for pancreatic disease.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "What is pancreatitis?",
        body: `Pancreatitis is inflammation of the pancreas. Acute attacks present with severe upper-abdominal pain radiating to the back, often with vomiting. Chronic pancreatitis presents with recurrent pain, weight loss and steatorrhoea (fatty stools).` },
      { id: "causes", eyebrow: "Causes", heading: "What triggers it.",
        bullets: ["Gallstones", "Alcohol", "Hypertriglyceridaemia", "Drug-induced", "Post-ERCP", "Genetic (hereditary pancreatitis)", "Autoimmune", "Tropical pancreatitis (regional in India)"],
      },
      { id: "diagnosis", eyebrow: "Diagnosis", heading: "Lipase + imaging.",
        body: `Acute: serum lipase (or amylase) ≥3× upper limit + characteristic pain + imaging finding confirms diagnosis. CT scan grades severity. Chronic: history + imaging (CT/MRI/EUS) + functional tests (faecal elastase).` },
      { id: "treatment-acute", eyebrow: "Treatment — Acute", heading: "Admission, fluids, nutrition.",
        body: `Acute pancreatitis is treated with aggressive IV fluids, pain control, early oral feeding when tolerated, and treatment of the underlying cause (gallstone removal via ERCP, alcohol cessation, lipid control). Severe cases need ICU care.` },
      { id: "treatment-chronic", eyebrow: "Treatment — Chronic", heading: "Long-term management.",
        body: `Pain control, pancreatic enzyme supplementation, treatment of diabetes (Type 3c), nutritional support, and addressing pseudocysts/strictures. Selected patients benefit from ERCP intervention, EUS-guided drainage or surgery.`,
        sidenote: "Long-term alcohol abstinence is non-negotiable for alcohol-related pancreatitis — it slows progression and reduces flare-ups dramatically.",
      },
    ],
    faq: [
      { q: "How serious is acute pancreatitis?", a: "Mild cases recover in 5–7 days. Severe cases can be life-threatening — early aggressive treatment matters." },
      { q: "Will I have lifelong pain?", a: "Chronic pancreatitis pain is manageable in most patients with the right combination of pain control, enzymes and lifestyle changes. Some need endoscopic or surgical intervention." },
      { q: "Do I need enzyme supplements forever?", a: "If you have steatorrhoea or maldigestion — yes, indefinitely. They are safe and tolerated well." },
      { q: "Can I drink alcohol again?", a: "No. Even a small drink can precipitate a flare. Complete abstinence is required." },
    ],
    related: [
      { label: "ERCP", to: "/procedures/ercp" },
      { label: "Endoscopic Ultrasound (EUS)", to: "/procedures/eus" },
      { label: "Abdominal Pain", to: "/symptoms/abdominal-pain-specialist" },
    ],
    schema: {
      name: "Pancreatitis",
      alternateName: ["Acute Pancreatitis", "Chronic Pancreatitis"],
      signOrSymptom: ["Severe upper abdominal pain", "Pain radiating to back", "Vomiting", "Steatorrhoea (chronic)", "Weight loss (chronic)"],
      riskFactor: ["Gallstones", "Alcohol", "Hypertriglyceridaemia", "Drug-induced", "Post-ERCP", "Genetic"],
      possibleTreatment: ["IV fluid resuscitation", "Pain control", "ERCP for gallstone removal", "Pancreatic enzyme supplementation", "Alcohol cessation"],
    },
  },

  "ulcerative-colitis-treatment": {
    shortTitle: "Ulcerative Colitis",
    title: "Ulcerative Colitis Treatment in Varanasi",
    subtitle:
      "An inflammatory bowel disease of the colon. With the right plan, most patients live without flares for years.",
    seoTitle: "Ulcerative Colitis Treatment in Varanasi — Dr. Indresh Dixit (IBD)",
    seoDescription:
      "Long-term Ulcerative Colitis management in Varanasi — mesalamine, immunosuppressives, biologics and JAK inhibitors with Dr. Indresh Dixit.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "Continuous inflammation, colon only.",
        body: `Ulcerative Colitis (UC) is a chronic inflammatory disease of the colon. Inflammation begins in the rectum and extends proximally in a continuous manner. It is a relapsing-remitting illness — long quiet phases punctuated by flares.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "Bloody diarrhoea is the hallmark.",
        bullets: ["Bloody / mucousy diarrhoea", "Lower abdominal pain", "Urgency, tenesmus", "Weight loss, low-grade fever in flare", "Joint pain, skin or eye involvement (extra-intestinal)"],
      },
      { id: "diagnosis", eyebrow: "Diagnosis", heading: "Colonoscopy with biopsy.",
        body: `Colonoscopy with multiple biopsies confirms diagnosis and assesses extent and severity. Stool tests rule out infection. Calprotectin tracks mucosal inflammation. CT/MRI enterography is reserved for atypical or extensive disease.` },
      { id: "treatment", eyebrow: "Treatment", heading: "Step-up therapy.",
        body: `Mild-moderate disease: oral + topical mesalamine. Moderate-severe: corticosteroids for flare, immunomodulators (azathioprine), biologics (anti-TNF, vedolizumab, ustekinumab) or JAK inhibitors. Severe or refractory disease may need surgery.`,
        sidenote: "Long-standing extensive UC (>8 years) needs surveillance colonoscopy every 1–2 years to screen for dysplasia/cancer.",
      },
      { id: "lifestyle", eyebrow: "Lifestyle", heading: "What helps day-to-day.",
        bullets: ["Symptom diary helps spot flares early", "Smoking cessation if recently started — paradoxically smoking sometimes worsens steroid withdrawal", "Stress management and sleep hygiene", "Avoid NSAID painkillers", "Vaccinations (live vaccines need timing care)"],
      },
    ],
    faq: [
      { q: "Is UC curable?", a: "Medically — long remission is the realistic goal. Surgical removal of the colon (proctocolectomy) cures the disease, reserved for refractory or complicated cases." },
      { q: "Can I have a normal pregnancy?", a: "Yes — ideally conceive in remission. Most UC medications are pregnancy-safe; we plan this carefully." },
      { q: "Diet for UC?", a: "No single 'UC diet'. During flares — low residue, soft, lactose-aware. In remission — full healthy diet, individualised." },
      { q: "Will I need biologics?", a: "Only if mesalamine and steroids don't control disease. Biologics are very effective for moderate-severe UC." },
    ],
    related: [
      { label: "Crohn's Disease", to: "/diseases/crohns-disease-treatment" },
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
      { label: "Blood in Stool", to: "/symptoms/blood-in-stool-treatment" },
    ],
    schema: {
      name: "Ulcerative Colitis",
      alternateName: ["UC", "Inflammatory Bowel Disease"],
      signOrSymptom: ["Bloody diarrhoea", "Lower abdominal pain", "Urgency", "Weight loss", "Joint pain"],
      riskFactor: ["Family history of IBD", "Genetic susceptibility", "Western diet patterns"],
      possibleTreatment: ["Mesalamine", "Corticosteroids", "Azathioprine", "Anti-TNF biologics", "Vedolizumab", "JAK inhibitors", "Colectomy in refractory disease"],
    },
  },

  "crohns-disease-treatment": {
    shortTitle: "Crohn's Disease",
    title: "Crohn's Disease Treatment in Varanasi",
    subtitle:
      "An IBD that can affect any part of the GI tract — from mouth to anus. Diagnosis is sometimes delayed; treatment is highly effective when started early.",
    seoTitle: "Crohn's Disease Treatment in Varanasi — Dr. Indresh Dixit (IBD)",
    seoDescription:
      "Specialist Crohn's disease care in Varanasi — biologics, immunosuppressives and small-bowel imaging with Dr. Indresh Dixit.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "Skip lesions across the GI tract.",
        body: `Crohn's is a chronic, full-thickness, patchy inflammation that can occur anywhere in the GI tract — most commonly the terminal ileum. It causes complications like fistulas, strictures and abscesses.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "Often vague at the start.",
        bullets: ["Chronic abdominal pain (often right-lower)", "Diarrhoea (sometimes bloody)", "Weight loss, anaemia", "Mouth ulcers, joint pain, perianal disease", "Fistulas, abscesses (later)"],
      },
      { id: "diagnosis", eyebrow: "Diagnosis", heading: "Ileocolonoscopy + small-bowel imaging.",
        body: `Colonoscopy with terminal ileum intubation + biopsies is the cornerstone. Small-bowel disease is mapped with MR enterography or capsule endoscopy. Calprotectin tracks activity. Bloods include iron, B12, CRP and serum drug levels in biologic-treated patients.` },
      { id: "treatment", eyebrow: "Treatment", heading: "Stop inflammation, prevent damage.",
        body: `Early biologic therapy (anti-TNF, ustekinumab, vedolizumab) plus immunomodulators where indicated. Corticosteroids only for induction in flare — not maintenance. Surgical resection for stricturing or fistulising disease as needed.`,
        sidenote: "Smoking worsens Crohn's significantly — cessation is one of the most powerful interventions.",
      },
      { id: "nutrition", eyebrow: "Nutrition & long-term", heading: "Replenish and reassess.",
        body: `Iron and B12 deficiency are common. Exclusive enteral nutrition is effective for inducing remission, particularly in children. Long-term — bone density screening, vaccination updates, fertility counselling, and structured follow-up.` },
    ],
    faq: [
      { q: "Is Crohn's curable?", a: "Currently no cure. With modern therapy, long durable remission is realistic. Treatment goals have moved from symptom control to mucosal healing." },
      { q: "Will I need surgery?", a: "About half of Crohn's patients need surgery at some point — usually for strictures, fistulas or abscesses. Modern therapy reduces this risk." },
      { q: "Are biologics safe?", a: "Yes, with appropriate screening and monitoring. The benefits in moderate-severe Crohn's far outweigh risks." },
      { q: "Can I plan pregnancy?", a: "Yes — ideally in remission, on a pregnancy-compatible regimen. We plan this together." },
    ],
    related: [
      { label: "Ulcerative Colitis", to: "/diseases/ulcerative-colitis-treatment" },
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
      { label: "Abdominal Pain", to: "/symptoms/abdominal-pain-specialist" },
    ],
    schema: {
      name: "Crohn's Disease",
      alternateName: ["Crohn Disease", "Inflammatory Bowel Disease"],
      signOrSymptom: ["Chronic abdominal pain", "Diarrhoea", "Weight loss", "Mouth ulcers", "Perianal disease", "Fistulas"],
      riskFactor: ["Family history of IBD", "Smoking", "Genetic susceptibility"],
      possibleTreatment: ["Anti-TNF biologics", "Ustekinumab", "Vedolizumab", "Immunomodulators", "Corticosteroids for flare", "Exclusive enteral nutrition", "Surgical resection"],
    },
  },

  "peptic-ulcer-treatment": {
    shortTitle: "Peptic Ulcer",
    title: "Peptic Ulcer Treatment in Varanasi",
    subtitle:
      "An open sore in the lining of the stomach or duodenum. With the right diagnosis it heals reliably; missed, it can bleed or perforate.",
    seoTitle: "Peptic Ulcer Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription:
      "H. pylori eradication and modern PPI-based peptic ulcer treatment in Varanasi by Dr. Indresh Dixit. Endoscopy in-clinic.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "Gastric vs duodenal ulcers.",
        body: `Peptic ulcers are open sores in the stomach (gastric) or duodenum (duodenal). H. pylori and NSAIDs are responsible for the vast majority. Gastric ulcers always need biopsy and follow-up endoscopy to confirm healing — to rule out cancer.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "Classic patterns.",
        bullets: ["Burning epigastric pain", "Duodenal ulcer: pain relieved by food, returns 2–3 hours later", "Gastric ulcer: pain often worsens with food", "Nausea, occasional vomiting", "Black stool / vomiting blood (complication)"],
      },
      { id: "diagnosis", eyebrow: "Diagnosis", heading: "Endoscopy + H. pylori testing.",
        body: `Upper GI Endoscopy visualises the ulcer, allows biopsy (mandatory for gastric ulcers) and tests for H. pylori. Stool antigen / urea breath test are non-invasive alternatives.` },
      { id: "treatment", eyebrow: "Treatment", heading: "Reliable healing pathway.",
        body: `PPI for 4–8 weeks for duodenal ulcers, longer for gastric ulcers. H. pylori eradication (14-day combination). Stop NSAIDs if causative. Follow-up endoscopy for gastric ulcers to confirm healing.`,
        sidenote: "Active bleeding ulcer needs urgent endoscopic therapy — clipping, injection or coagulation — performed in the same procedure.",
      },
      { id: "complications", eyebrow: "Complications", heading: "What to watch for.",
        bullets: ["Bleeding (haematemesis or melena)", "Perforation (sudden severe pain, rigid abdomen)", "Gastric outlet obstruction (vomiting undigested food)", "Recurrent ulcers if H. pylori not eradicated"],
      },
    ],
    faq: [
      { q: "How long for an ulcer to heal?", a: "Duodenal ulcers: 4 weeks of PPI. Gastric ulcers: 8 weeks, with confirmatory endoscopy." },
      { q: "Will it come back?", a: "Recurrence is rare if H. pylori is fully eradicated and NSAIDs are avoided." },
      { q: "Can I take painkillers?", a: "Paracetamol is fine. NSAIDs (ibuprofen, diclofenac) should be avoided; if essential, combined with PPI cover." },
      { q: "Is surgery needed?", a: "Rarely — mainly for complications like perforation, uncontrollable bleeding or obstruction." },
    ],
    related: [
      { label: "Gastritis", to: "/diseases/gastritis-treatment" },
      { label: "Upper GI Endoscopy", to: "/procedures/upper-gi-endoscopy" },
      { label: "Hematemesis", to: "/diseases/hematemesis-treatment" },
    ],
    schema: {
      name: "Peptic Ulcer Disease",
      alternateName: ["Gastric Ulcer", "Duodenal Ulcer"],
      signOrSymptom: ["Burning epigastric pain", "Pain pattern with meals", "Nausea", "Anaemia", "Melena or haematemesis"],
      riskFactor: ["H. pylori infection", "NSAID use", "Smoking", "Alcohol", "Stress"],
      possibleTreatment: ["PPI therapy", "H. pylori eradication", "Endoscopic therapy for bleeding", "Follow-up endoscopy for gastric ulcers"],
    },
  },

  "hemorrhoids-piles-treatment": {
    shortTitle: "Hemorrhoids (Piles)",
    title: "Hemorrhoids (Piles) Treatment in Varanasi",
    subtitle:
      "Painful, itchy or bleeding piles are extremely common — and almost always fixable without surgery when caught early. The trick is not ignoring them.",
    seoTitle:
      "Hemorrhoids (Piles) Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription:
      "Non-surgical piles / hemorrhoids treatment in Varanasi. Grading, banding, sclerotherapy and surgery referral where needed with Dr. Indresh Dixit.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "Internal vs external piles.",
        body: `Hemorrhoids (piles) are swollen veins in the anal canal and around the anus. Internal piles usually cause painless bleeding; external piles cause pain, swelling and itching. Both are graded from I to IV depending on prolapse.\n\nAlmost every adult will experience some form at some point — but persistent or bleeding piles need a proper evaluation, not just an over-the-counter cream.` },
      { id: "symptoms", eyebrow: "Symptoms", heading: "How they present.",
        bullets: [
          "Bright red blood on toilet paper or in the pan",
          "A soft lump felt near the anus",
          "Pain, worse with sitting or motion (external)",
          "Itching or wetness around the anus",
          "Feeling of incomplete evacuation",
          "Prolapse — piles coming out during motion",
        ],
      },
      { id: "causes", eyebrow: "Why they happen", heading: "Causes and triggers.",
        bullets: [
          "Chronic constipation and straining",
          "Low-fibre / low-water diet",
          "Long hours of sitting or standing",
          "Pregnancy",
          "Heavy lifting",
          "Chronic cough or diarrhoea",
          "Family history / weak vein walls",
        ],
      },
      { id: "diagnosis", eyebrow: "Diagnosis", heading: "Never assume — always confirm.",
        body: `A quick clinical exam plus proctoscopy in the clinic confirms piles and grades them. For anyone above 40, with a family history of colon cancer, or with anaemia — we add a full colonoscopy to rule out polyps or cancer that can look identical from the outside.`,
        sidenote: "Bright red bleeding is not always \"just piles\". Anyone over 40 with new rectal bleeding — or younger with weight loss / anaemia — must have a colonoscopy.",
      },
      { id: "treatment", eyebrow: "Treatment", heading: "Non-surgical first, always.",
        body: `Grade I–II: high-fibre diet + adequate water + Sitz baths + topical anaesthetic/steroid + laxative if constipation is present. Most patients settle in 2–3 weeks.\n\nGrade II–III unresponsive to lifestyle: in-clinic rubber-band ligation or sclerotherapy — walk-in, walk-out, no cuts.\n\nGrade III–IV / recurrent: surgical referral for stapled hemorrhoidopexy or laser hemorrhoidoplasty.` },
      { id: "prevent", eyebrow: "Prevention", heading: "Keep them from coming back.",
        bullets: [
          "25–30 g of dietary fibre daily (Isabgol / vegetables / oats)",
          "2.5–3 L of water daily",
          "Move every hour — avoid long sitting",
          "Do not strain on the toilet; limit to 5 minutes",
          "Treat any diarrhoea or constipation early",
        ],
      },
    ],
    faq: [
      { q: "Is surgery always needed for piles?", a: "No — in over 80% of patients we resolve piles without surgery through diet, medicines and in-clinic banding or sclerotherapy." },
      { q: "How long does rubber-band ligation take?", a: "5–10 minutes in the clinic; you walk out the same day. Some discomfort for 24–48 hours is normal." },
      { q: "Can I still get colon cancer with piles?", a: "Yes. Piles and colon cancer can coexist — that's why we screen anyone over 40 with rectal bleeding or family history." },
      { q: "Are piles related to spicy food?", a: "Not directly, but very spicy food can worsen the burning symptom during motions. Fibre and hydration matter far more." },
    ],
    related: [
      { label: "Blood in Stool", to: "/symptoms/blood-in-stool-treatment" },
      { label: "Chronic Constipation", to: "/symptoms/constipation-specialist-varanasi" },
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
    ],
    schema: {
      name: "Hemorrhoids (Piles)",
      alternateName: ["Piles", "Haemorrhoids", "Internal hemorrhoids", "External hemorrhoids"],
      signOrSymptom: [
        "Bright red rectal bleeding",
        "Anal lump or swelling",
        "Pain on sitting",
        "Anal itching",
        "Prolapse during motion",
      ],
      riskFactor: [
        "Chronic constipation",
        "Low-fibre diet",
        "Prolonged sitting or standing",
        "Pregnancy",
        "Heavy lifting",
        "Chronic diarrhoea",
      ],
      possibleTreatment: [
        "High-fibre diet and hydration",
        "Sitz baths and topical creams",
        "Rubber-band ligation",
        "Sclerotherapy",
        "Surgical referral for advanced grades",
      ],
    },
  },

  "hematemesis-treatment": {
    shortTitle: "Hematemesis (Vomiting Blood)",
    title: "Hematemesis (Vomiting Blood) Treatment in Varanasi",
    subtitle:
      "Vomiting blood — bright red or coffee-ground brown — is a medical emergency. Get to a hospital first; the causes are treatable, but time matters.",
    seoTitle:
      "Hematemesis (Vomiting Blood) Treatment in Varanasi — Dr. Indresh Dixit",
    seoDescription:
      "Emergency evaluation and treatment of hematemesis (vomiting blood) in Varanasi. Urgent endoscopy for ulcer bleed, varices, Mallory-Weiss and gastritis with Dr. Indresh Dixit.",
    sections: [
      { id: "what", eyebrow: "Overview", heading: "Two patterns — both need urgent care.",
        body: `Hematemesis means vomiting blood. Fresh red blood suggests ongoing bleeding from the esophagus, stomach or duodenum. \"Coffee-ground\" vomit is blood that has been partly digested — the bleeding may have slowed but is still recent.\n\nBoth require immediate hospital evaluation. Do not wait to see if it repeats.`,
        sidenote: "Emergency step: go to the nearest hospital or call the clinic immediately. Do NOT eat, drink or take painkillers before evaluation.",
      },
      { id: "causes", eyebrow: "Common causes", heading: "What is usually behind it.",
        bullets: [
          "Peptic ulcer bleed (most common)",
          "Erosive gastritis (NSAIDs, alcohol)",
          "Esophageal or gastric varices (in cirrhosis)",
          "Mallory-Weiss tear (violent retching)",
          "Esophagitis with ulceration",
          "Gastric or esophageal cancer (uncommon)",
          "Angiodysplasia / vascular lesions",
        ],
      },
      { id: "assessment", eyebrow: "First hospital assessment", heading: "Stabilise first, scope next.",
        body: `Priorities are IV access, fluid or blood resuscitation, IV PPI, and — where variceal bleed is suspected — vasoactive drugs (terlipressin/octreotide) and antibiotic cover. Once stable, an urgent Upper GI Endoscopy identifies the source and treats it in the same session.` },
      { id: "endoscopy", eyebrow: "Endoscopic treatment", heading: "The same scope that finds it, stops it.",
        body: `Ulcer bleed: injection therapy, thermal coagulation, clips.\n\nVarices: endoscopic band ligation or sclerotherapy.\n\nMallory-Weiss tear: often stops on its own; endoscopic clip if actively bleeding.\n\nErosive gastritis: high-dose IV PPI + treat cause.\n\nMost patients are stable within 24 hours and discharged in 2–3 days.` },
      { id: "long-term", eyebrow: "After the bleed", heading: "Preventing the next one.",
        bullets: [
          "H. pylori testing and eradication if ulcer",
          "Stop NSAIDs; alternatives arranged",
          "In cirrhotic patients: variceal surveillance + beta-blocker",
          "Complete alcohol cessation",
          "PPI course to allow full healing",
          "Repeat endoscopy at 6–8 weeks for gastric ulcers",
        ],
      },
    ],
    faq: [
      { q: "Is vomiting blood always life-threatening?", a: "It always deserves emergency evaluation. Many episodes settle with prompt treatment; but a delayed presentation can be dangerous. Do not wait to see if it happens again." },
      { q: "What should I do first?", a: "Go to the nearest hospital immediately. Do not eat, drink, take antacids or painkillers. Bring a list of current medicines and any known liver disease details." },
      { q: "Can I be discharged home the same day?", a: "Only after an endoscopy has confirmed the source is controlled and no active bleeding remains. Most patients stay for 24–72 hours." },
      { q: "Will I need long-term medicine?", a: "Usually yes — PPI for weeks, H. pylori course if positive, and lifelong measures if variceal disease was the cause." },
    ],
    related: [
      { label: "Peptic Ulcer", to: "/diseases/peptic-ulcer-treatment" },
      { label: "Liver Cirrhosis", to: "/diseases/liver-cirrhosis-treatment" },
      { label: "Upper GI Endoscopy", to: "/procedures/upper-gi-endoscopy" },
    ],
    schema: {
      name: "Hematemesis (Upper GI Bleeding)",
      alternateName: ["Vomiting Blood", "Haematemesis", "Upper GI Bleed", "UGI Bleed"],
      signOrSymptom: [
        "Vomiting bright red blood",
        "Coffee-ground vomit",
        "Black tarry stool (melena)",
        "Dizziness or fainting",
        "Rapid heart rate",
        "Pallor",
      ],
      riskFactor: [
        "Peptic ulcer disease",
        "NSAID use",
        "Alcohol excess",
        "Cirrhosis with varices",
        "Severe vomiting (Mallory-Weiss)",
        "Anticoagulant use",
      ],
      possibleTreatment: [
        "Emergency IV resuscitation",
        "IV proton pump inhibitor",
        "Urgent Upper GI Endoscopy",
        "Endoscopic haemostasis (clips, thermal, injection)",
        "Variceal band ligation",
        "H. pylori eradication",
      ],
    },
  },
};
